import {
  Checkbox,
  Col,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Typography,
} from 'antd';

import {
  CheckboxGroupButtons,
  CompaniesInfiniteSelectField,
  FloatingFormItem,
} from '@/components/_common';
import { useIntl } from 'react-intl';

import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';
import {
  StockMinimumFilterBy,
  StockMinimumFormValues,
  StockMinimumTypes,
} from '@/core/domain/reports/stock/Report.types';
import { useDefaultEmptyFieldError } from '@/hooks';

interface Props {
  form: FormInstance<StockMinimumFormValues>;
}

const MinimumStockForm = ({ form }: Props) => {
  const intl = useIntl().formatMessage;
  const { Title } = Typography;

  const requiredField = useDefaultEmptyFieldError();

  const filterBy = Form.useWatch('filterBy', form);

  const MinimumStockOptions = [
    {
      value: StockMinimumFilterBy.sku,
      label: intl({ id: 'stock.minimumStock.form.filterBy.internalCode' }),
    },
    {
      value: StockMinimumFilterBy.manufacturingCode,
      label: intl({ id: 'stock.minimumStock.form.filterBy.manufacturingCode' }),
    },
    {
      value: StockMinimumFilterBy.barCode,
      label: intl({ id: 'stock.minimumStock.form.filterBy.barCode' }),
    },
    {
      value: StockMinimumFilterBy.description,
      label: intl({ id: 'stock.minimumStock.form.filterBy.description' }),
    },
    {
      value: StockMinimumFilterBy.manufacturing,
      label: intl({ id: 'stock.minimumStock.form.filterBy.manufacturer' }),
    },
    {
      value: StockMinimumFilterBy.group,
      label: intl({ id: 'stock.minimumStock.form.filterBy.group' }),
    },
    {
      value: StockMinimumFilterBy.subGroup,
      label: intl({ id: 'stock.minimumStock.form.filterBy.subGroup' }),
    },
  ];

  const showFromToFields = [
    StockMinimumFilterBy.sku,
    StockMinimumFilterBy.manufacturingCode,
    StockMinimumFilterBy.barCode,
    StockMinimumFilterBy.description,
  ].includes(filterBy);

  return (
    <Form layout="vertical" form={form}>
      <Row gutter={[24, 24]}>
        <Col xs={13}>
          <FloatingFormItem
            label={intl({ id: 'stock.minimumStock.form.companies' })}
            name="companiesIds"
          >
            <CompaniesInfiniteSelectField
              allowClear
              mode="multiple"
              style={{ width: '100%' }}
              queryFilter={{ rows: 50 }}
              queryEnable
              selectAll
            />
          </FloatingFormItem>
        </Col>

        <Col xs={12}>
          <Form.Item name="minimumStock" valuePropName="checked" noStyle>
            <Checkbox>
              {intl({ id: 'stock.minimumStock.form.displayMinimumStock' })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="below" valuePropName="checked" noStyle>
            <Checkbox>
              {intl({ id: 'stock.minimumStock.form.belowStock' })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="isActiveItems"
            valuePropName="checked"
            noStyle
            initialValue={true}
          >
            <Checkbox>
              {intl({ id: 'stock.minimumStock.form.isActiveItems' })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="isInactiveItems" valuePropName="checked" noStyle>
            <Checkbox>
              {intl({ id: 'stock.minimumStock.form.isInactiveItems' })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="isMinimumStockGreaterThanZero"
            valuePropName="checked"
            initialValue={true}
            noStyle
          >
            <Checkbox>
              Exibir Peças e/ou insumos com estoque mínimo maior que zero
            </Checkbox>
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="isMinimumStockEqualToZero"
            valuePropName="checked"
            initialValue={false}
            noStyle
          >
            <Checkbox>
              Exibir Peças e/ou insumos com estoque mínimo igual a zero
            </Checkbox>
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Title level={5}>
            {intl({ id: 'stock.minimumStock.form.partTypes' })}
          </Title>
          <Form.Item name="type" initialValue={[StockMinimumTypes.part]}>
            <CheckboxGroupButtons
              options={[
                {
                  value: StockMinimumTypes.part,
                  label: intl({
                    id: 'stock.minimumStock.form.partTypes.parts',
                  }),
                },
                {
                  value: StockMinimumTypes.consumable,
                  label: intl({
                    id: 'stock.minimumStock.form.partTypes.inputs',
                  }),
                },
              ]}
            />
          </Form.Item>
        </Col>

        <Col xs={8}>
          <FloatingFormItem
            name="filterBy"
            label={intl({ id: 'stock.minimumStock.form.filterBy' })}
          >
            <Select options={MinimumStockOptions} allowClear />
          </FloatingFormItem>
        </Col>

        <Form.Item noStyle shouldUpdate>
          {() => {
            if (showFromToFields) {
              return (
                <>
                  <Col xs={8}>
                    <FloatingFormItem
                      name="filterFrom"
                      label={intl({ id: 'stock.minimumStock.form.in' })}
                      rules={[requiredField]}
                    >
                      <Input />
                    </FloatingFormItem>
                  </Col>

                  <Col xs={8}>
                    <FloatingFormItem
                      name="filterTo"
                      label={intl({ id: 'stock.minimumStock.form.upUntil' })}
                      rules={[requiredField]}
                    >
                      <Input />
                    </FloatingFormItem>
                  </Col>
                </>
              );
            }

            if (filterBy === StockMinimumFilterBy.manufacturing) {
              return (
                <Col xs={8}>
                  <FloatingFormItem
                    rules={[requiredField]}
                    name="manufacturerIds"
                    label="Fabricante"
                  >
                    <ManufacturerInfiniteSelectField mode="multiple" />
                  </FloatingFormItem>
                </Col>
              );
            }

            if (filterBy === StockMinimumFilterBy.group) {
              return (
                <Col xs={8}>
                  <FloatingFormItem
                    rules={[requiredField]}
                    name="groupIds"
                    label="Grupo"
                  >
                    <GroupsInfiniteSelectField onlyGroups mode="multiple" />
                  </FloatingFormItem>
                </Col>
              );
            }

            if (filterBy === StockMinimumFilterBy.subGroup) {
              return (
                <Col xs={8}>
                  <FloatingFormItem
                    rules={[requiredField]}
                    name="subGroupsIds"
                    label={'Sub Grupo'}
                  >
                    <GroupsInfiniteSelectField mode="multiple" />
                  </FloatingFormItem>
                </Col>
              );
            }
          }}
        </Form.Item>
      </Row>
    </Form>
  );
};

export default MinimumStockForm;
